/* eslint-disable max-len */
import PageLayout from 'components/Layouts/PageLayout/PageLayout';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as VMIcon } from 'assets/icons_3/large/server-01.svg';
import { ReactComponent as NetworkIcon } from 'assets/icons_3/large/server-05.svg';
import { ReactComponent as FirewallIcon } from 'assets/icons_3/large/shield-tick.svg';
import { ReactComponent as LoadbalancerIcon } from 'assets/icons_3/large/dataflow-01.svg';
import { ReactComponent as TemplateIcon } from 'assets/icons_3/large/disc-02.svg';
import { ReactComponent as DiskIcon } from 'assets/icons_3/large/server-03.svg';
import { ReactComponent as ClusterIcon } from 'assets/icons_3/large/Kubernetes.svg';
import { ReactComponent as BucketIcon } from 'assets/icons_3/large/box.svg';
import { ReactComponent as ArrowUpRight } from 'assets/icons_3/arrows/arrow-up-right.svg';
import { ReactComponent as ArrowRight } from 'assets/icons_3/arrows/arrow-left.svg';
import { ReactComponent as ArrowLeft } from 'assets/icons_3/arrows/arrow-right.svg';
import { useAppSelector } from 'hooks';
import classNames from 'classnames';
import PageHeader from 'components/Layouts/PageHeader/PageHeader';
import useAsyncFetch from 'hooks/async-fetch';
import profileService from 'services/api/customer/profile-service';
import { LimitedVMParam } from 'store/virtual-machine/types/state.types';
import RouterLinkButton from 'components/UI_kit_3/Button/RouterLinkButton';
import LinkButton from 'components/UI_kit_3/Button/LinkButton';
import Button from 'components/UI_kit_3/Button/Button';
import Tooltip from 'components/UI_kit_3/Popover/Tooltip/Tooltip';
import { ServicePermissions } from 'components/Project/types/enums';
import { usePermission } from 'hooks/use-permission';
import styles from './CloudPlatform.module.scss';

const CloudPlatform = () => {
  const [t] = useTranslation();
  const user = useAppSelector((state) => state.user);
  const { hasAnyPermission, isAnyReadPermission } = usePermission();
  const countryCode = useAppSelector((state) => state.user.country?.code);
  const containerRef = useRef<HTMLDivElement>(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateState = () => {
    const el = containerRef.current;
    if (!el) return;

    setAtStart(el.scrollLeft <= 0);
    setAtEnd(Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth);
  };

  const scrollLeft = () => {
    const el = containerRef.current;
    if (!el) return;

    el.scrollBy({
      left: -el.clientWidth,
      behavior: 'smooth',
    });
  };

  const scrollRight = () => {
    const el = containerRef.current;
    if (!el) return;

    el.scrollBy({
      left: el.clientWidth,
      behavior: 'smooth',
    });
  };

  const [,, resources] = useAsyncFetch({
    async asyncFunc() {
      return (await profileService.getDashboardResources(user.region?.id)).data.data;
    },
    immediate: true,
    initialState: null,
  });

  const diskSizes = React.useMemo(() => {
    const hhdTotal = resources?.quotas.find((res) => res.resourceName === LimitedVMParam.HDDSize)?.totalLimit || 0;
    const ssdTotal = resources?.quotas.find((res) => res.resourceName === LimitedVMParam.SSDSize)?.totalLimit || 0;
    const nvmeTotal = resources?.quotas.find((res) => res.resourceName === LimitedVMParam.NVMESize)?.totalLimit || 0;
    const storageTotal = resources?.quotas.find((res) => res.resourceName === LimitedVMParam.Storage)?.totalLimit || 0;

    const sizes = {
      [LimitedVMParam.HDDSize]: {
        value: resources?.totalHddGb || 0,
        total: hhdTotal,
        label: t('file_size.gb'),
      },
      [LimitedVMParam.SSDSize]: {
        value: resources?.totalSsdGb || 0,
        total: ssdTotal,
        label: t('file_size.gb'),
      },
      [LimitedVMParam.NVMESize]: {
        value: resources?.totalNvmeGb || 0,
        total: nvmeTotal,
        label: t('file_size.gb'),
      },
      [LimitedVMParam.Storage]: {
        value: resources?.totalObjectStorageGb || 0,
        total: storageTotal,
        label: t('file_size.gb'),
      },
    };
    if (hhdTotal >= 1024) {
      sizes[LimitedVMParam.HDDSize].total = parseFloat((hhdTotal / 1024).toFixed(2));
      sizes[LimitedVMParam.HDDSize].value = parseFloat((sizes[LimitedVMParam.HDDSize].value / 1024).toFixed(2));
      sizes[LimitedVMParam.HDDSize].label = t('file_size.tb');
    }
    if (ssdTotal >= 1024) {
      sizes[LimitedVMParam.SSDSize].total = parseFloat((ssdTotal / 1024).toFixed(2));
      sizes[LimitedVMParam.SSDSize].value = parseFloat((sizes[LimitedVMParam.SSDSize].value / 1024).toFixed(2));
      sizes[LimitedVMParam.SSDSize].label = t('file_size.tb');
    }
    if (nvmeTotal >= 1024) {
      sizes[LimitedVMParam.NVMESize].total = parseFloat((nvmeTotal / 1024).toFixed(2));
      sizes[LimitedVMParam.NVMESize].value = parseFloat((sizes[LimitedVMParam.NVMESize].value / 1024).toFixed(2));
      sizes[LimitedVMParam.NVMESize].label = t('file_size.tb');
    }
    if (storageTotal >= 1024) {
      sizes[LimitedVMParam.Storage].total = parseFloat((storageTotal / 1024).toFixed(2));
      sizes[LimitedVMParam.Storage].value = parseFloat((sizes[LimitedVMParam.Storage].value / 1024).toFixed(2));
      sizes[LimitedVMParam.Storage].label = t('file_size.tb');
    }

    return sizes;
  }, [resources, t]);

  const cards = [
    {
      id: 'vm',
      name: t('vm.self_plural_long'),
      value: resources?.vmCount,
      link: '/services/servers',
    },
    {
      id: LimitedVMParam.CPUCores,
      name: t(`report.resources.${LimitedVMParam.CPUCores}`),
      value: resources?.totalCpuCores,
      total: resources?.quotas.find((res) => res.resourceName === LimitedVMParam.CPUCores)?.totalLimit,
    },
    {
      id: LimitedVMParam.RAM,
      name: `${t(`report.resources.${LimitedVMParam.RAM}`)}, ${t('common.gb')}`,
      value: resources?.totalRamGb,
      total: resources?.quotas.find((res) => res.resourceName === LimitedVMParam.RAM)?.totalLimit,
    },
    {
      id: LimitedVMParam.PublicIP,
      name: t(`report.resources.${LimitedVMParam.PublicIP}`),
      value: resources?.totalPublicIps,
      total: resources?.quotas.find((res) => res.resourceName === LimitedVMParam.PublicIP)?.totalLimit,
      link: '/services/networks',
      state: { tab: 'public' },
    },
    {
      id: LimitedVMParam.LocalNetworks,
      name: t(`report.resources.${LimitedVMParam.LocalNetworks}`),
      value: resources?.totalLocalNetworks,
      total: resources?.quotas.find((res) => res.resourceName === LimitedVMParam.LocalNetworks)?.totalLimit,
      link: '/services/networks',
    },
    {
      id: LimitedVMParam.Backup,
      name: `${t(`report.resources.${LimitedVMParam.Backup}`)}`,
      value: resources?.totalBackupCount,
      total: resources?.quotas.find((res) => res.resourceName === LimitedVMParam.Backup)?.totalLimit,
      link: '/services/backups',
    },
    {
      id: LimitedVMParam.HDDSize,
      name: `${t(`report.resources.${LimitedVMParam.HDDSize}`)}, ${diskSizes[LimitedVMParam.HDDSize].label}`,
      value: diskSizes[LimitedVMParam.HDDSize].value,
      total: diskSizes[LimitedVMParam.HDDSize].total,
      link: '/services/disks',
    },
    {
      id: LimitedVMParam.SSDSize,
      name: `${t(`report.resources.${LimitedVMParam.SSDSize}`)}, ${diskSizes[LimitedVMParam.SSDSize].label}`,
      value: diskSizes[LimitedVMParam.SSDSize].value,
      total: diskSizes[LimitedVMParam.SSDSize].total,
      link: '/services/disks',
    },
    {
      id: LimitedVMParam.NVMESize,
      name: `${t(`report.resources.${LimitedVMParam.NVMESize}.${countryCode}`)}, ${diskSizes[LimitedVMParam.NVMESize].label}`,
      value: diskSizes[LimitedVMParam.NVMESize].value,
      total: diskSizes[LimitedVMParam.NVMESize].total,
      link: '/services/disks',
    },
    {
      id: LimitedVMParam.Storage,
      name: `${t(`report.resources.${LimitedVMParam.Storage}`)}, ${diskSizes[LimitedVMParam.Storage].label}`,
      value: diskSizes[LimitedVMParam.Storage].value,
      total: diskSizes[LimitedVMParam.Storage].total,
      link: '/services/storage',
    },
  ];

  const createLinks = [
    {
      title: t('dashboard.create.vm'),
      Icon: VMIcon,
      link: '/services/servers/create',
    },
    {
      title: t('dashboard.create.localNetwork'),
      Icon: NetworkIcon,
      link: '/services/networks',
    },
    {
      title: t('dashboard.create.security_group'),
      Icon: FirewallIcon,
      link: '/services/security-groups/create',
    },
    {
      title: t('dashboard.create.loadbalancer'),
      Icon: LoadbalancerIcon,
      link: '/services/loadbalancers/create',
    },
    {
      title: t('dashboard.create.disk'),
      Icon: DiskIcon,
      link: '/services/disks',
    },
    {
      title: t('dashboard.create.template'),
      Icon: TemplateIcon,
      link: '/services/templates',
    },
    {
      title: t('dashboard.create.cluster'),
      Icon: ClusterIcon,
      link: '/services/clusters/new',
    },
    {
      title: t('dashboard.create.bucket'),
      Icon: BucketIcon,
      link: '/services/storage/bucket/create',
    },
  ];

  useEffect(() => {
    updateState();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return () => {};

    el.addEventListener('scroll', updateState);
    window.addEventListener('resize', updateState);

    return () => {
      el.removeEventListener('scroll', updateState);
      window.removeEventListener('resize', updateState);
    };
  }, []);

  return (
    <PageLayout title={t('navigation.main_page')}>
      <PageHeader className="mb-xl" title={t('resources.self_long')}>
        <div className={styles.sliderControls} qa-element="resources_slider_controls">
          <Button disabled={atStart} variant="secondary" prefixIcon={<ArrowRight />} onClick={scrollLeft} qa-element="resources_slider_left_arrow" />
          <Button disabled={atEnd} variant="secondary" prefixIcon={<ArrowLeft />} onClick={scrollRight} qa-element="resources_slider_right_arrow" />
        </div>
      </PageHeader>
      <div ref={containerRef} className={classNames(styles.resources, 'mb-3xl pb-xl')}>
        {cards.map((card) => (
          <div key={card.id} className={styles.card}>
            <h6>{card.name}</h6>
            <div className={styles.cardFooter}>
              <Tooltip
                text={t('dashboard.taken')}
                active={card.total !== undefined}
              >
                <span className={styles.cardValue}>{card.value}</span>
              </Tooltip>
              {card.total !== undefined && (
              <>
                <span className={styles.cardTotal}>/</span>
                <Tooltip text={t('dashboard.total')}>
                  <span className={styles.cardTotal}>{`${card.total}`}</span>
                </Tooltip>
              </>
              )}
              {card.link && (
              <RouterLinkButton
                variant="link"
                className={styles.resourcesLink}
                to={card.link}
                suffixIcon={<ArrowUpRight />}
                disabled={isAnyReadPermission([ServicePermissions.VIRTUAL_SERVERS, ServicePermissions.NETWORK, ServicePermissions.DISKS, ServicePermissions.BACKUPS, ServicePermissions.S3]) || !hasAnyPermission([ServicePermissions.VIRTUAL_SERVERS, ServicePermissions.NETWORK, ServicePermissions.DISKS, ServicePermissions.BACKUPS, ServicePermissions.S3])}
                qa-element={`resource_${card.id}_link`}
                state={card.state || null}
              >
                {t('common.forward')}
              </RouterLinkButton>
              )}
            </div>
          </div>
        ))}
      </div>
      <h2 className={styles.header}>{t('dashboard.create_something_new')}</h2>
      <div className={classNames(styles.create, 'mb-4xl')}>
        {createLinks.map((createLink) => (
          <div key={createLink.link} className={styles.createLink}>
            <div className={styles.createIcon}>
              <createLink.Icon />
            </div>
            <RouterLinkButton
              variant="link"
              className={styles.stretchedLink}
              to={createLink.link}
              state={{ create: true }}
              disabled={isAnyReadPermission([ServicePermissions.VIRTUAL_SERVERS, ServicePermissions.NETWORK, ServicePermissions.SECURITY_GROUPS, ServicePermissions.LOAD_BALANCERS, ServicePermissions.DISKS, ServicePermissions.CUSTOM_IMAGES, ServicePermissions.KUBERNETES]) || !hasAnyPermission([ServicePermissions.VIRTUAL_SERVERS, ServicePermissions.NETWORK, ServicePermissions.DISKS, ServicePermissions.BACKUPS, ServicePermissions.S3])}
              qa-element={`create_${createLink.link}_link`}
            >
              {createLink.title}
            </RouterLinkButton>
          </div>
        ))}
      </div>
      <h2 className={styles.header}>{t('dashboard.useful_info.self')}</h2>
      <div className={styles.usefulInfo}>
        <div className={styles.usefulInfoCard}>
          <h6>{t('dashboard.useful_info.vm.self')}</h6>
          <p>{t('dashboard.useful_info.vm.description')}</p>
          <LinkButton variant="primary" size="xl" href="https://helpdesk.pro-data.tech/portal/ru/kb/%D0%B2%D0%B8%D1%80%D1%82%D1%83%D0%B0%D0%BB%D1%8C%D0%BD%D1%8B%D0%B5-%D1%81%D0%B5%D1%80%D0%B2%D0%B5%D1%80%D1%8B-%D1%81-%D0%BF%D0%B0%D0%BD%D0%B5%D0%BB%D1%8C%D1%8E-%D1%83%D0%BF%D1%80%D0%B0%D0%B2%D0%BB%D0%B5%D0%BD%D0%B8%D1%8F" qa-element="useful_info_vm_knowledge_base">{t('dashboard.useful_info.knowledge_base')}</LinkButton>
        </div>
        <div className={styles.usefulInfoCard}>
          <h6>{t('dashboard.useful_info.k8s.self')}</h6>
          <p>{t('dashboard.useful_info.k8s.description')}</p>
          <LinkButton variant="primary" size="xl" href="https://helpdesk.pro-data.tech/portal/ru/kb/managed-kubernetes" qa-element="useful_info_k8s_knowledge_base">{t('dashboard.useful_info.knowledge_base')}</LinkButton>
        </div>
        <div className={styles.usefulInfoCard}>
          <h6>{t('dashboard.useful_info.faq.self')}</h6>
          <p>{t('dashboard.useful_info.faq.description')}</p>
          <LinkButton variant="primary" size="xl" href="https://helpdesk.pro-data.tech/portal/ru/kb/faq" qa-element="useful_info_faq_knowledge_base">{t('dashboard.useful_info.knowledge_base')}</LinkButton>
        </div>
      </div>
    </PageLayout>
  );
};

export default CloudPlatform;
